import React, { useEffect, useState, useCallback } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from '../context/Web3Context';
import { useToast } from '../components/Toast';
import { fetchBlacklist, addToBlacklist, removeFromBlacklist } from '../utils/blacklistApi';
import { formatAddress } from '../utils/helpers';
import './Admin.css';

const Admin = () => {
  const { account } = useWeb3();
  const toast = useToast();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newAddress, setNewAddress] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [removing, setRemoving] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    const items = await fetchBlacklist();
    setList(items);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const addr = newAddress.trim();
    if (!ethers.utils.isAddress(addr)) {
      toast.error('Invalid Ethereum address');
      return;
    }
    if (list.includes(addr.toLowerCase())) {
      toast.warning('Address is already blacklisted');
      return;
    }
    setSubmitting(true);
    const res = await addToBlacklist(account, addr);
    setSubmitting(false);
    if (res && res.success) {
      toast.success('Address blacklisted');
      setNewAddress('');
      if (Array.isArray(res.data)) setList(res.data);
      else loadList();
    } else {
      toast.error(res?.message || 'Failed to add address');
    }
  };

  const handleRemove = async (addr) => {
    if (!window.confirm(`Remove ${addr} from the blacklist?`)) return;
    setRemoving(addr);
    const res = await removeFromBlacklist(account, addr);
    setRemoving(null);
    if (res && res.success) {
      toast.success('Address removed');
      if (Array.isArray(res.data)) setList(res.data);
      else loadList();
    } else {
      toast.error(res?.message || 'Failed to remove address');
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header">
        <h1 className="page-title">
          <span className="title-icon">🛡️</span>
          Admin Panel
        </h1>
        <p className="admin-sub">Blacklist Management</p>
      </div>

      <form className="admin-add-card" onSubmit={handleAdd}>
        <label className="admin-label">Add address to blacklist</label>
        <div className="admin-input-row">
          <input
            className="admin-input"
            type="text"
            placeholder="0x..."
            value={newAddress}
            onChange={(e) => setNewAddress(e.target.value)}
            spellCheck="false"
            autoComplete="off"
            disabled={submitting}
          />
          <button
            type="submit"
            className="admin-btn admin-btn-primary"
            disabled={submitting || !newAddress}
          >
            {submitting ? 'Adding...' : 'Add'}
          </button>
        </div>
      </form>

      <div className="admin-list-card">
        <div className="admin-list-header">
          <span>Blacklisted addresses</span>
          <span className="admin-count">{list.length}</span>
        </div>

        {loading ? (
          <div className="admin-empty">Loading...</div>
        ) : list.length === 0 ? (
          <div className="admin-empty">No addresses blacklisted.</div>
        ) : (
          <ul className="admin-list">
            {list.map((addr) => (
              <li key={addr} className="admin-list-item">
                <span className="admin-addr" title={addr}>
                  {formatAddress(addr, 10, 8)}
                </span>
                <button
                  className="admin-btn admin-btn-danger"
                  onClick={() => handleRemove(addr)}
                  disabled={removing === addr}
                >
                  {removing === addr ? 'Removing...' : 'Remove'}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Admin;
